/**
 * The learner's Career DNA™ check-in schedule.
 *
 * A membership year holds five walks: one when you join, then one every three
 * months, with a final one at the end of the year. The database is the rule —
 * `get_my_assessment_schedule()` computes the slots and a trigger refuses any
 * take that arrives early or beyond the fifth. This module is the reading side:
 * it turns those slots into plain sentences the app can show.
 */
import { supabase } from "@/integrations/supabase/client";

export const MAX_ASSESSMENT_TAKES = 5;
export const CHECKIN_INTERVAL_DAYS = 90;

export type SlotStatus = "completed" | "in_progress" | "available" | "locked";

export interface ScheduleSlot {
  index: number;
  key: string;
  label: string;
  unlocksAt: string | null;
  status: SlotStatus;
  assessmentId: string | null;
}

export interface AssessmentSchedule {
  membershipStartedAt: string | null;
  slots: ScheduleSlot[];
  /** Walks already finished. */
  completed: number;
  /** Walks still to come this year. */
  remaining: number;
  /** The next slot the learner can act on, when there is one. */
  next: ScheduleSlot | null;
  /** True when the next walk is waiting on a date. */
  locked: boolean;
  /** True when all five are done — time to talk about the year ahead. */
  cycleComplete: boolean;
  /** When the membership year ends, if we know when it started. */
  cycleEndsAt: string | null;
}

const EMPTY: AssessmentSchedule = {
  membershipStartedAt: null,
  slots: [],
  completed: 0,
  remaining: MAX_ASSESSMENT_TAKES,
  next: null,
  locked: false,
  cycleComplete: false,
  cycleEndsAt: null,
};

type RawSlot = {
  index?: number;
  key?: string;
  label?: string;
  unlocks_at?: string | null;
  status?: string;
  assessment_id?: string | null;
};

function normalise(raw: unknown): AssessmentSchedule {
  const obj = (raw ?? {}) as { membership_started_at?: string | null; slots?: RawSlot[] };
  const slots: ScheduleSlot[] = (Array.isArray(obj.slots) ? obj.slots : []).map((s, i) => ({
    index: typeof s.index === "number" ? s.index : i,
    key: s.key ?? `slot-${i}`,
    label: s.label ?? `Check-in ${i + 1}`,
    unlocksAt: s.unlocks_at ?? null,
    status: (["completed", "in_progress", "available", "locked"] as const).includes(
      s.status as SlotStatus,
    )
      ? (s.status as SlotStatus)
      : "locked",
    assessmentId: s.assessment_id ?? null,
  }));

  if (!slots.length) return EMPTY;

  const completed = slots.filter((s) => s.status === "completed").length;
  const next = slots.find((s) => s.status !== "completed") ?? null;
  const started = obj.membership_started_at ?? null;
  const cycleEndsAt = started
    ? new Date(new Date(started).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString()
    : null;

  return {
    membershipStartedAt: started,
    slots,
    completed,
    remaining: Math.max(0, MAX_ASSESSMENT_TAKES - completed),
    next,
    locked: next?.status === "locked",
    cycleComplete: completed >= MAX_ASSESSMENT_TAKES,
    cycleEndsAt,
  };
}

/** Read the schedule for the signed-in learner. */
export async function fetchAssessmentSchedule(): Promise<AssessmentSchedule> {
  const { data, error } = await supabase.rpc("get_my_assessment_schedule");
  if (error) {
    // eslint-disable-next-line no-console
    console.error("[wcc360.schedule] read failed:", error.message);
    return EMPTY;
  }
  return normalise(data);
}

/** Days from now until a date, never negative. */
export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** One warm sentence about where the learner stands. */
export function scheduleHeadline(s: AssessmentSchedule): string {
  if (s.cycleComplete) return "You've completed every check-in this year";
  if (!s.next) return "Your check-ins are ready when you are";
  if (s.next.status === "in_progress") return "You're partway through this check-in";
  if (s.next.status === "available")
    return s.completed === 0 ? "Your first walk is open" : "Your next check-in is open";
  const days = daysUntil(s.next.unlocksAt);
  if (days === null) return "Your next check-in opens soon";
  return days <= 1 ? "Your next check-in opens tomorrow" : `Your next check-in opens in ${days} days`;
}
